import { gsap, ScrollTrigger, screenSm } from './1.1_animations-defaults.js';

import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from '../../tailwind.config.js';

const HERO_SECTION_ID = 'full-screen-hero';
const LETTER_CONTAINER_CLASS = 'aos';
const LOADED_CLASS = 'loaded';
const BG_IMAGES_CLASS = 'background-images';
const LOADER_CLASS = 'loader';
const INIT_DELAY = 0;
const INTERVAL = 600;
const FINAL_DELAY = 0;
const COOKIE_NAME = 'hero-section';
const COOKIE_VALUE = 'shown';
const BODY_HERO_SLIDE_SHOWN_CLASS = 'full-screen-hero-shown';
const BODY_HERO_SLIDE_END_CLASS = 'full-screen-hero-end';
const BODY_HERO_SLIDE_ENDED_CLASS = 'full-screen-hero-ended';

class FullScreenHero {
    constructor() {
        this._heroSection = document.getElementById(HERO_SECTION_ID);
        this._theme = resolveConfig(tailwindConfig).theme;

        this._run();
    }
    _run = () => {
        if (this._heroSection) {
            this._days = this._heroSection.dataset.reappearAfter;
            this._loader = this._heroSection.querySelector(`.${LOADER_CLASS}`);
            this._bgImageSlides = this._heroSection.querySelectorAll(`.${BG_IMAGES_CLASS}>*`);

            if (!this._isShown()) {
                this._disableScroll();
                this._showLoader();
                addEventListener('load', this._init);
            } else {
                this._heroSection.classList.add('hidden');
                requestAnimationFrame(() => {
                    document.body.classList.add(BODY_HERO_SLIDE_SHOWN_CLASS);
                });
            }
        } else {
            requestAnimationFrame(() => {
                document.body.classList.add(BODY_HERO_SLIDE_SHOWN_CLASS);
            });
        }
    };

    _delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    _disableScroll = () => {
        document.body.classList.add('fixed');
    };

    _enableScroll = () => {
        document.body.classList.remove('fixed');
    };

    _activateSlide = async (bgImageSlide, firstSlide) => {
        !firstSlide && (await this._delay(INTERVAL));
        requestAnimationFrame(() => {
            bgImageSlide.classList.add('!block');

            const svgs = this._heroSection.querySelectorAll('svg');

            svgs.forEach((svg) => {
                svg.style.fill = this._getThemeColor(bgImageSlide.dataset.textColour);
            });
        });
    };

    _showLoader = () => {
        this._loader.classList.remove('invisible');
    };

    _hideLoader = () => {
        this._loader.classList.add('invisible');
    };

    _slideUp = () => {
        this._heroSection.classList.add('duration-[2000ms]', '-translate-y-full');
        document.body.classList.add(BODY_HERO_SLIDE_END_CLASS);
        const transitionEnd = () => {
            this._enableScroll();
            document.body.classList.add(BODY_HERO_SLIDE_ENDED_CLASS);
            requestAnimationFrame(() => {
                ScrollTrigger.refresh();
            });
            this._heroSection.removeEventListener('transitionend', transitionEnd);
        };
        this._heroSection.addEventListener('transitionend', transitionEnd);
    };

    _getThemeColor = (colorName) => {
        return this._theme.colors[`${colorName}`];
    };

    _setCookie = (name, value, days) => {
        const d = new Date();
        d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
        const expires = 'expires=' + d.toUTCString();
        document.cookie = name + '=' + value + ';' + expires + ';path=/';
    };

    _getCookie = (name) => {
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookies = decodedCookie.split(';');
        name = name + '=';
        for (let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i].trim();
            if (cookie.indexOf(name) === 0) {
                return cookie.substring(name.length, cookie.length);
            }
        }
        return false;
    };

    _isShown = () => {
        const cookieName = COOKIE_NAME;

        if (!this._getCookie(cookieName)) {
            this._setCookie(cookieName, COOKIE_VALUE, this._days);
            return false;
        } else {
            return true;
        }
    };

    _init = async () => {
        requestAnimationFrame(() => {
            this._hideLoader();
            this._heroSection.classList.add(LOADED_CLASS);
        });
        await this._delay(INIT_DELAY);

        const lastLetter = this._heroSection.querySelector(`.${LETTER_CONTAINER_CLASS}>*:last-child`);
        lastLetter.addEventListener('transitionend', async () => {
            let firstSlide = true;
            for (const bgImageSlide of this._bgImageSlides) {
                await this._activateSlide(bgImageSlide, firstSlide);
                firstSlide = false;
            }
            await this._delay(FINAL_DELAY);
            this._slideUp();
        });
    };
}

const fullScreenHero = new FullScreenHero();
